import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, AnimatePresence, animate, PanInfo } from 'framer-motion';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
}

interface Metric {
  value: string;
  label: string;
}

interface TestimonialsSectionProps {
  headline?: string;
  testimonials: Testimonial[];
  metrics?: Metric[];
}

// Extended testimonial data with additional fields
const extendedTestimonialData: Record<string, {
  avatar: string;
  videoThumbnail?: string;
  hasVideo: boolean;
  rating: number;
  verified: boolean;
  companyLogo: string;
  stats: { label: string; value: string }[];
  color: string;
}> = {
  'James Chen': {
    avatar: 'JC',
    hasVideo: true,
    videoThumbnail: '',
    rating: 5,
    verified: true,
    companyLogo: 'TechFlow',
    stats: [
      { label: 'Time Saved', value: '340 hrs/mo' },
      { label: 'ROI', value: '847%' },
      { label: 'Team Size', value: '230' },
    ],
    color: 'from-blue-500 to-cyan-500',
  },
  'Maria Santos': {
    avatar: 'MS',
    hasVideo: true,
    videoThumbnail: '',
    rating: 5,
    verified: true,
    companyLogo: 'HealthTech',
    stats: [
      { label: 'Annual Savings', value: '$340K' },
      { label: 'Processes Automated', value: '12' },
      { label: 'Team Size', value: '180' },
    ],
    color: 'from-violet-500 to-purple-500',
  },
  'David Park': {
    avatar: 'DP',
    hasVideo: false,
    rating: 5,
    verified: true,
    companyLogo: 'FinServe',
    stats: [
      { label: 'Market Lead', value: '6 mo' },
      { label: 'Deployment', value: '19 days' },
      { label: 'Team Size', value: '320' },
    ],
    color: 'from-emerald-500 to-teal-500',
  },
};

// Company logos for background cloud
const companyLogos = [
  'TechFlow', 'HealthTech', 'FinServe', 'DataCorp', 'CloudScale',
  'AIVentures', 'SmartOps', 'NextGen', 'Innovate', 'DigiTech',
];

// Star Rating Component with golden gradient fill
const StarRating: React.FC<{ rating: number; isActive: boolean }> = ({ rating, isActive }) => {
  return (
    <div className="flex gap-1">
      {[...Array(5)].map((_, index) => (
        <motion.div
          key={index}
          className="relative w-5 h-5"
          initial={{ scale: 0, rotate: -180 }}
          animate={isActive ? { scale: 1, rotate: 0 } : { scale: 0.8, rotate: 0 }}
          transition={{ delay: index * 0.1, type: 'spring', stiffness: 300 }}
        >
          {/* Star background */}
          <svg className="w-5 h-5 text-zinc-700" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>

          {/* Star fill with gradient */}
          <motion.div
            className="absolute inset-0"
            initial={{ clipPath: 'inset(100% 0 0 0)' }}
            animate={isActive && index < rating ? { clipPath: 'inset(0% 0 0 0)' } : { clipPath: 'inset(100% 0 0 0)' }}
            transition={{ delay: 0.5 + index * 0.15, duration: 0.4, ease: 'easeOut' }}
          >
            <svg className="w-5 h-5" fill="url(#starGradient)" viewBox="0 0 20 20">
              <defs>
                <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#fbbf24" />
                  <stop offset="50%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#d97706" />
                </linearGradient>
              </defs>
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
};

// Verified Badge with checkmark animation
const VerifiedBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  return (
    <motion.div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0.5, scale: 0.9 }}
      transition={{ delay: 0.3, type: 'spring', stiffness: 300 }}
    >
      <motion.div
        className="relative w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center"
        initial={{ scale: 0 }}
        animate={isActive ? { scale: 1 } : { scale: 0.8 }}
        transition={{ delay: 0.4, type: 'spring', stiffness: 500 }}
      >
        <motion.svg
          className="w-2.5 h-2.5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          initial={{ pathLength: 0 }}
          animate={isActive ? { pathLength: 1 } : { pathLength: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
        >
          <motion.path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
          />
        </motion.svg>
      </motion.div>
      <span className="text-emerald-400 text-xs font-medium">Verified</span>
    </motion.div>
  );
};

// Quote with typewriter effect
const TypewriterQuote: React.FC<{ quote: string; isActive: boolean }> = ({ quote, isActive }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (isActive && !isComplete) {
      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex <= quote.length) {
          setDisplayedText(quote.slice(0, currentIndex));
          currentIndex++;
        } else {
          setIsComplete(true);
          clearInterval(interval);
        }
      }, 20);
      return () => clearInterval(interval);
    } else if (!isActive) {
      setDisplayedText(quote);
      setIsComplete(true);
    }
  }, [isActive, quote, isComplete]);

  return (
    <div className="relative">
      {/* Animated quote marks */}
      <motion.span
        className="absolute -left-4 -top-2 text-4xl text-emerald-500/20 font-serif"
        initial={{ opacity: 0, scale: 0.5, rotate: -20 }}
        animate={isActive ? { opacity: 1, scale: 1, rotate: 0 } : { opacity: 0.3, scale: 0.8 }}
        transition={{ duration: 0.5 }}
      >
        "
      </motion.span>

      <blockquote className="text-lg sm:text-xl text-zinc-300 leading-relaxed pl-2">
        {displayedText}
        {!isComplete && isActive && (
          <motion.span
            className="inline-block w-0.5 h-5 bg-emerald-400 ml-0.5"
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 0.8, repeat: Infinity }}
          />
        )}
      </blockquote>

      <motion.span
        className="absolute -right-2 bottom-0 text-4xl text-emerald-500/20 font-serif"
        initial={{ opacity: 0, scale: 0.5, rotate: 20 }}
        animate={isActive ? { opacity: 1, scale: 1, rotate: 0 } : { opacity: 0.3, scale: 0.8 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        "
      </motion.span>
    </div>
  );
};

// Video Thumbnail with play button glow
const VideoThumbnail: React.FC<{ isActive: boolean; gradient: string }> = ({ isActive, gradient }) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <motion.div
      className="relative w-20 h-20 rounded-xl overflow-hidden cursor-pointer"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {/* Thumbnail background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-30`} />
      <div className="absolute inset-0 bg-zinc-900/50" />

      {/* Play button with glow */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="relative"
          animate={isActive && isHovering ? { scale: [1, 1.1, 1] } : {}}
          transition={{ duration: 1, repeat: Infinity }}
        >
          {/* Glow effect */}
          <motion.div
            className="absolute inset-0 bg-white rounded-full blur-md"
            animate={{ opacity: isHovering ? 0.5 : 0.2, scale: isHovering ? 1.5 : 1 }}
            transition={{ duration: 0.3 }}
          />

          {/* Play icon */}
          <div className="relative w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
            <svg className="w-4 h-4 text-zinc-900 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </motion.div>
      </div>

      {/* Video badge */}
      <motion.div
        className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-red-500 rounded text-[10px] font-bold text-white"
        animate={{ opacity: isHovering ? 1 : 0.7 }}
      >
        VIDEO
      </motion.div>
    </motion.div>
  );
};

// Client Stats that appear on hover
const ClientStats: React.FC<{
  stats: { label: string; value: string }[];
  isVisible: boolean;
}> = ({ stats, isVisible }) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 10, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: 10, height: 0 }}
          transition={{ duration: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
          className="overflow-hidden"
        >
          <div className="grid grid-cols-3 gap-4 pt-4 mt-4 border-t border-zinc-800/50">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-lg font-bold text-white">{stat.value}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Author Headshot with parallax
const AuthorHeadshot: React.FC<{
  initials: string;
  gradient: string;
  isActive: boolean;
  mouseX: number;
  mouseY: number;
}> = ({ initials, gradient, isActive, mouseX, mouseY }) => {
  // Parallax effect based on mouse position
  const offsetX = (mouseX - 50) * 0.1;
  const offsetY = (mouseY - 50) * 0.1;

  return (
    <motion.div
      className="relative"
      animate={{
        x: isActive ? offsetX : 0,
        y: isActive ? offsetY : 0,
      }}
      transition={{ type: 'spring', stiffness: 150, damping: 15 }}
    >
      {/* Outer glow */}
      <motion.div
        className={`absolute -inset-2 rounded-full bg-gradient-to-r ${gradient} blur-lg`}
        animate={{ opacity: isActive ? 0.4 : 0 }}
        transition={{ duration: 0.3 }}
      />

      {/* Avatar ring */}
      <motion.div
        className={`relative w-16 h-16 rounded-full bg-gradient-to-r ${gradient} p-[2px]`}
        animate={{ scale: isActive ? 1 : 0.9 }}
        transition={{ type: 'spring', stiffness: 300 }}
      >
        <div className="w-full h-full rounded-full bg-zinc-900 flex items-center justify-center">
          <span className="text-xl font-bold text-white">{initials}</span>
        </div>
      </motion.div>

      {/* Online indicator */}
      <motion.div
        className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-zinc-900"
        initial={{ scale: 0 }}
        animate={isActive ? { scale: 1 } : { scale: 0 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 500 }}
      />
    </motion.div>
  );
};

// Floating Logo Cloud
const LogoCloud: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {companyLogos.map((logo, index) => {
        const randomX = 10 + (index % 5) * 20;
        const randomY = 10 + Math.floor(index / 5) * 40;
        const delay = index * 0.5;

        return (
          <motion.div
            key={logo}
            className="absolute text-zinc-800/30 text-sm font-semibold"
            style={{ left: `${randomX}%`, top: `${randomY}%` }}
            animate={{
              y: [0, -10, 0],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 4 + index * 0.3,
              repeat: Infinity,
              delay,
              ease: 'easeInOut',
            }}
          >
            {logo}
          </motion.div>
        );
      })}
    </div>
  );
};

// 3D Carousel Card
const CarouselCard: React.FC<{
  testimonial: Testimonial;
  index: number;
  activeIndex: number;
  totalCards: number;
  onActivate: () => void;
  isPaused: boolean;
}> = ({ testimonial, index, activeIndex, totalCards, onActivate, isPaused }) => {
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isHovering, setIsHovering] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const extendedData = extendedTestimonialData[testimonial.author] || extendedTestimonialData['James Chen'];
  const isActive = index === activeIndex;

  // Calculate 3D position
  const offset = index - activeIndex;
  const absOffset = Math.abs(offset);

  // Wrap around for infinite carousel feel
  let adjustedOffset = offset;
  if (offset > totalCards / 2) adjustedOffset = offset - totalCards;
  if (offset < -totalCards / 2) adjustedOffset = offset + totalCards;

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!cardRef.current || !isActive) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  }, [isActive]);

  return (
    <motion.div
      ref={cardRef}
      className={`absolute top-0 left-1/2 w-full max-w-2xl cursor-pointer ${
        isActive ? 'z-30' : absOffset === 1 ? 'z-20' : 'z-10'
      }`}
      style={{
        transformStyle: 'preserve-3d',
        perspective: 1200,
      }}
      animate={{
        x: `calc(-50% + ${adjustedOffset * 120}px)`,
        scale: isActive ? 1 : 0.85 - absOffset * 0.1,
        rotateY: adjustedOffset * -15,
        z: isActive ? 100 : -absOffset * 100,
        opacity: absOffset > 1 ? 0.3 : 1,
        filter: isActive ? 'blur(0px)' : `blur(${absOffset * 2}px)`,
      }}
      transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
      onClick={onActivate}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setMousePosition({ x: 50, y: 50 });
      }}
    >
      {/* Card */}
      <motion.div
        className="relative rounded-2xl overflow-hidden"
        animate={{
          rotateX: isActive && isHovering ? (mousePosition.y - 50) * -0.1 : 0,
          rotateY: isActive && isHovering ? (mousePosition.x - 50) * 0.1 : 0,
        }}
        transition={{ duration: 0.2 }}
      >
        {/* Animated gradient border */}
        <motion.div
          className={`absolute -inset-[1px] rounded-2xl bg-gradient-to-r ${extendedData.color} opacity-0`}
          animate={{ opacity: isActive ? 0.6 : 0 }}
          style={{ backgroundSize: '200% 200%' }}
          transition={{ duration: 0.3 }}
        />

        {/* Rotating gradient for active card */}
        {isActive && (
          <motion.div
            className={`absolute -inset-[1px] rounded-2xl bg-gradient-to-r ${extendedData.color}`}
            style={{ backgroundSize: '200% 200%' }}
            animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          />
        )}

        {/* Glass morphism background */}
        <div className="absolute inset-[1px] rounded-2xl bg-zinc-900/95 backdrop-blur-xl" />

        {/* Inner shadow */}
        <div
          className="absolute inset-[1px] rounded-2xl pointer-events-none"
          style={{
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05), inset 0 -1px 2px rgba(0,0,0,0.4)',
          }}
        />

        {/* Cursor highlight */}
        {isActive && (
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              background: `radial-gradient(600px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(52, 211, 153, 0.1), transparent 40%)`,
            }}
            animate={{ opacity: isHovering ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />
        )}

        {/* Content */}
        <div className="relative z-10 p-8">
          {/* Top row: Avatar, Rating, Verified, Video */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <AuthorHeadshot
                initials={extendedData.avatar}
                gradient={extendedData.color}
                isActive={isActive}
                mouseX={mousePosition.x}
                mouseY={mousePosition.y}
              />

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-semibold">{testimonial.author}</span>
                  {extendedData.verified && <VerifiedBadge isActive={isActive} />}
                </div>
                <p className="text-zinc-500 text-sm">{testimonial.role}</p>
                <p className="text-zinc-600 text-sm">{testimonial.company}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {extendedData.hasVideo && (
                <VideoThumbnail isActive={isActive} gradient={extendedData.color} />
              )}
            </div>
          </div>

          {/* Star rating */}
          <div className="mb-4">
            <StarRating rating={extendedData.rating} isActive={isActive} />
          </div>

          {/* Quote with typewriter */}
          <div className="mb-6">
            {isActive ? (
              <TypewriterQuote quote={testimonial.quote} isActive={isActive && !isPaused} />
            ) : (
              <blockquote className="text-lg text-zinc-400 leading-relaxed line-clamp-3">
                "{testimonial.quote}"
              </blockquote>
            )}
          </div>

          {/* Company stats (visible on hover when active) */}
          <ClientStats stats={extendedData.stats} isVisible={isActive && isHovering} />
        </div>
      </motion.div>
    </motion.div>
  );
};

// Navigation Arrow Button
const NavigationArrow: React.FC<{
  direction: 'left' | 'right';
  onClick: () => void;
}> = ({ direction, onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      className="absolute top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-zinc-800/80 border border-zinc-700/50 backdrop-blur-sm flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700/80 hover:border-zinc-600 transition-colors"
      style={{ [direction]: '1rem' }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        {direction === 'left' ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        )}
      </svg>
    </motion.button>
  );
};

// Navigation Dots
const NavigationDots: React.FC<{
  total: number;
  activeIndex: number;
  onSelect: (index: number) => void;
}> = ({ total, activeIndex, onSelect }) => {
  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      {[...Array(total)].map((_, index) => (
        <motion.button
          key={index}
          className={`relative h-2 rounded-full transition-all ${
            index === activeIndex ? 'w-8' : 'w-2'
          }`}
          onClick={() => onSelect(index)}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.9 }}
        >
          <div className={`absolute inset-0 rounded-full ${
            index === activeIndex ? 'bg-emerald-500' : 'bg-zinc-700'
          }`} />

          {/* Progress indicator for active dot */}
          {index === activeIndex && (
            <motion.div
              className="absolute inset-0 rounded-full bg-emerald-400"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 5, ease: 'linear' }}
              style={{ transformOrigin: 'left' }}
            />
          )}
        </motion.button>
      ))}
    </div>
  );
};

// Metric card with premium effects
const MetricCard: React.FC<{
  metric: Metric;
  index: number;
}> = ({ metric, index }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [displayValue, setDisplayValue] = useState(metric.value);
  const [hasAnimated, setHasAnimated] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(cardRef, { once: true, margin: '-50px' });

  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
      const numericValue = parseFloat(metric.value.replace(/[^0-9.]/g, ''));
      const prefix = metric.value.match(/^[^0-9]*/)?.[0] || '';
      const suffix = metric.value.match(/[^0-9]*$/)?.[0] || '';

      if (!isNaN(numericValue) && numericValue > 0) {
        // Start from a low value for animation effect
        setDisplayValue(`${prefix}0${suffix}`);
        animate(0, numericValue, {
          duration: 2,
          delay: index * 0.2,
          onUpdate: (value) => {
            setDisplayValue(`${prefix}${Math.floor(value)}${suffix}`);
          },
        });
      } else {
        setDisplayValue(metric.value);
      }
    }
  }, [isInView, metric.value, index, hasAnimated]);

  return (
    <motion.div
      ref={cardRef}
      className="text-center group cursor-default"
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
      whileHover={{ scale: 1.05, y: -5 }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <p
        className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-2 ${
          isHovering ? 'text-emerald-400' : 'text-white'
        } transition-colors`}
      >
        {displayValue}
      </p>
      <p className="text-zinc-500 text-sm uppercase tracking-wider group-hover:text-zinc-400 transition-colors">
        {metric.label}
      </p>
    </motion.div>
  );
};

// Main Component
export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  headline = 'What Our Clients Say',
  testimonials,
  metrics,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // Auto-rotate carousel
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isPaused, testimonials.length]);

  // Handle drag for swipe
  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 50;

    if (info.offset.x > threshold) {
      setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    } else if (info.offset.x < -threshold) {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-16 sm:py-24 md:py-32 bg-[#0a0a0a] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Logo cloud background */}
      <LogoCloud />

      {/* Animated background blobs */}
      <motion.div
        className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[150px]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
          x: [0, 50, 0],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-teal-500/5 rounded-full blur-[120px]"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
          y: [0, -30, 0],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">Testimonials</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4"
          >
            {headline}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-zinc-400 text-lg"
          >
            Trusted by leading companies worldwide
          </motion.p>
        </div>

        {/* 3D Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          <NavigationArrow
            direction="left"
            onClick={() => setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
          />
          <NavigationArrow
            direction="right"
            onClick={() => setActiveIndex((prev) => (prev + 1) % testimonials.length)}
          />

          <motion.div
            ref={carouselRef}
            className="relative h-[500px] flex items-center justify-center"
            style={{ perspective: 1200, transformStyle: 'preserve-3d' }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.1}
            onDragEnd={handleDragEnd}
            whileTap={{ cursor: 'grabbing' }}
          >
            {testimonials.map((testimonial, index) => (
              <CarouselCard
                key={index}
                testimonial={testimonial}
                index={index}
                activeIndex={activeIndex}
                totalCards={testimonials.length}
                onActivate={() => setActiveIndex(index)}
                isPaused={isPaused}
              />
            ))}
          </motion.div>
        </div>

        {/* Navigation Dots */}
        <NavigationDots
          total={testimonials.length}
          activeIndex={activeIndex}
          onSelect={setActiveIndex}
        />

        {/* Metrics */}
        {metrics && metrics.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 md:gap-12 lg:gap-16 xl:gap-20 pt-10 sm:pt-12 md:pt-16 mt-10 sm:mt-12 md:mt-16 border-t border-zinc-800/50"
          >
            {metrics.map((metric, index) => (
              <MetricCard key={index} metric={metric} index={index} />
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
